import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Menu, MenuItem, Divider, Chip, useMediaQuery, useTheme, Tooltip,
} from '@mui/material';
import {
  Dashboard, People, EventNote, AttachMoney, BeachAccess,
  CalendarMonth, Business, Settings, Menu as MenuIcon,
  Logout, AccountCircle, ChevronLeft, Notifications, TableChart, AccessTime,
  History,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import logo from '../assets/logo.png';

const DRAWER_WIDTH = 260;

const ALL_NAV_ITEMS = [
  { label: 'Dashboard',     icon: <Dashboard />,      path: '/dashboard',    roles: ['all'] },
  { label: 'Employees',     icon: <People />,         path: '/employees',    roles: ['all'] },
  { label: 'Attendance',    icon: <EventNote />,      path: '/attendance',   roles: ['all'] },
  { label: 'Salary',        icon: <AttachMoney />,    path: '/salary',       roles: ['all'] },
  { label: 'Leave',         icon: <BeachAccess />,    path: '/leave',        roles: ['all'] },
  { label: 'Holidays',      icon: <CalendarMonth />,  path: '/holidays',     roles: ['all'] },
  { label: 'Departments',   icon: <Business />,       path: '/departments',  roles: ['admin'] },
  { label: 'Sessions',      icon: <AccessTime />,     path: '/sessions',     roles: ['all'] },
  { label: 'Users',         icon: <People />,         path: '/users',        roles: ['admin'] },
  { label: 'Notifications', icon: <Notifications />,  path: '/notifications',roles: ['admin'] },
  { label: 'Excel Export',  icon: <TableChart />,     path: '/exports',      roles: ['admin'] },
  { label: 'Audit Logs',    icon: <History />,        path: '/audit',        roles: ['all'] },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const isRestricted = !!(user?.department);
  const navItems = ALL_NAV_ITEMS.filter(item =>
    item.roles.includes('all') || (!isRestricted && item.roles.includes('admin'))
  );

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'linear-gradient(180deg, #1a237e 0%, #0d47a1 100%)', overflowY: 'auto'  }}>
      {/* Brand */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: 2, flexShrink: 0,
          background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: 0.5,
        }}>
          <img src={logo} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 4 }} />
        </Box>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>SN Worklog</Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>Management Panel</Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />

      {/* Role chip */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Chip
          label={user?.role || 'Admin'}
          size="small"
          sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 11 }}
        />
        {user?.name && (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mt: 0.5 }}>{user.name}</Typography>
        )}
        {user?.department && (
          <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>{user.department}</Typography>
        )}
      </Box>

      {/* Nav */}
      <List sx={{ flex: 1, px: 1.5, pt: 0 }}>
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => { navigate(item.path); setMobileOpen(false); }}
                sx={{
                  borderRadius: 2, py: 1.2,
                  background: active ? 'rgba(255,255,255,0.2)' : 'transparent',
                  '&:hover': { background: 'rgba(255,255,255,0.12)' },
                  border: active ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
                }}
              >
                <ListItemIcon sx={{ color: active ? '#fff' : 'rgba(255,255,255,0.65)', minWidth: 38 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: 14, fontWeight: active ? 700 : 500,
                    color: active ? '#fff' : 'rgba(255,255,255,0.75)',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />

      {/* Logout */}
      <List sx={{ px: 1.5, pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, py: 1.2, backgroundColor: 'red', '&:hover': { background: 'red' } }}>
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.65)', minWidth: 38 }}><Logout /></ListItemIcon>
            <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 14, fontWeight: 500, color: 'rgba(255,255,255,0.75)' }} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', background: '#f5f6fa' }}>
      {/* Sidebar */}
      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH, boxSizing: 'border-box', border: 'none',
              boxShadow: '4px 0 24px rgba(26,35,126,0.15)',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* AppBar */}
        <AppBar position="sticky" elevation={0} sx={{
          background: '#fff', borderBottom: '1px solid #e8eaf6',
          color: '#1a237e',
        }}>
          <Toolbar sx={{ gap: 1 }}>
            {isMobile && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: '#1a237e' }}>
                <MenuIcon />
              </IconButton>
            )}
            <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: '#1a237e', fontSize: { xs: 15, sm: 18 } }}>
              {navItems.find((n) => n.path === location.pathname)?.label || 'SN Worklog'}
            </Typography>

            <Tooltip title="Notifications">
              {/* <IconButton sx={{ color: '#1a237e' }}><Notifications /></IconButton> */}
            </Tooltip>

            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
              <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #1a237e, #1565c0)', fontSize: 14, fontWeight: 700 }}>
                {user?.role?.[0] || 'A'}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 180, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography fontWeight={700} fontSize={14}>{user?.name || user?.role}</Typography>
                <Typography fontSize={12} color="text.secondary">{user?.role}</Typography>
                {user?.department && (
                  <Typography fontSize={11} color="text.secondary">Dept: {user.department}</Typography>
                )}
              </Box>
              <Divider />
              <MenuItem onClick={handleLogout} sx={{ color: 'error.main', gap: 1 }}>
                <Logout fontSize="small" /> Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page content */}
        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
